import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, Droplet, Shuffle, Plus, Trash2, Pipette, X, ClipboardCheck } from "lucide-react";

/**
 * GlassColorPicker — GLASS THEME, clipboard-safe
 * ------------------------------------------------------
 * Fixes: Clipboard API can be blocked by Permissions Policy in sandboxed iframes.
 * This version adds a hardened `safeCopy()` that:
 *  1) Tries `navigator.clipboard.writeText` (if allowed)
 *  2) Falls back to a hidden `<textarea>` + `document.execCommand('copy')`
 *  3) If both fail, shows a manual-copy dialog with preselected text
 *
 * Other goodies preserved:
 *  - Uses your `.glass-effect` styles for that frosty look
 *  - HEX / RGB / HSL outputs + CSS vars snippet
 *  - Randomize color, tints/shades, saved swatches
 *  - Optional `attachToRoot` to live-wire :root CSS vars
 *  - `onChange(hex)` callback
 *
 * Exports a tiny test panel: <GlassColorPickerTestPanel />
 * to sanity-check color math in any environment.
 *
 * Usage example:
 * <GlassColorPicker attachToRoot onChange={(hex)=>console.log(hex)} />
 */

// ---------- utils ----------
function hexToRgb(hex) {
  let cleaned = hex.trim().replace(/^#/, "");
  if (cleaned.length === 3) cleaned = cleaned.split("").map((c) => c + c).join("");
  const num = parseInt(cleaned || "0", 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) { case r: h = (g - b) / d + (g < b ? 6 : 0); break; case g: h = (b - r) / d + 2; break; default: h = (r - g) / d + 4; }
    h *= 60;
  }
  return { h: Math.round(h || 0), s: Math.round((s || 0) * 100), l: Math.round((l || 0) * 100) };
}

const rgbString = ({ r, g, b }) => `rgb(${r}, ${g}, ${b})`;
const hslString = ({ h, s, l }) => `hsl(${h} ${s}% ${l}%)`;

function shade(hex, percent) {
  // percent: -100..100 (negative = darker, positive = lighter)
  const { r, g, b } = hexToRgb(hex);
  const t = percent < 0 ? 0 : 255; const p = Math.abs(percent) / 100;
  const R = Math.round((t - r) * p) + r;
  const G = Math.round((t - g) * p) + g;
  const B = Math.round((t - b) * p) + b;
  return `#${[R, G, B].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

const randomHex = () => `#${Math.floor(Math.random()*0xffffff).toString(16).padStart(6, "0")}`;

// Hardened copy helper with multi-level fallback and a final manual dialog trigger
async function safeCopy(text) {
  // 1) Try async Clipboard API
  if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    try {
      await navigator.clipboard.writeText(text);
      return { ok: true, mode: "clipboard" };
    } catch (_) {
      // swallow and try fallback
    }
  }
  // 2) Fallback to execCommand with hidden textarea (often allowed even when Clipboard API is blocked)
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) return { ok: true, mode: "execCommand" };
  } catch (_) {}
  // 3) Manual copy required
  return { ok: false, mode: "manual" };
}

// ---------- Copy Button (clipboard-safe) ----------
function CopyButton({ text, label = "Copy" }) {
  const [isCopied, setIsCopied] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (manualOpen && textRef.current) {
      // preselect for quick Cmd/Ctrl+C
      textRef.current.focus();
      textRef.current.select();
    }
  }, [manualOpen]);

  const handleCopy = async () => {
    const res = await safeCopy(text);
    if (res.ok) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1100);
    } else {
      setManualOpen(true);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleCopy}
        className="relative inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium border border-white/20 bg-white/10 hover:bg-white/15 active:scale-95 transition shadow-sm"
        aria-label={label}
      >
        <AnimatePresence initial={false} mode="wait">
          {isCopied ? (
            <motion.span key="copied" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="inline-flex items-center gap-1">
              <Check className="h-4 w-4" aria-hidden /> Copied
            </motion.span>
          ) : (
            <motion.span key="copy" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="inline-flex items-center gap-1">
              <Copy className="h-4 w-4" aria-hidden /> Copy
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Manual copy dialog */}
      <AnimatePresence>
        {manualOpen && (
          <motion.div className="fixed inset-0 z-50 grid place-items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/50" onClick={() => setManualOpen(false)} />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="relative w-[min(92vw,560px)] rounded-2xl glass-effect border border-white/20 p-4 shadow-2xl"
              role="dialog" aria-modal="true" aria-label="Manual copy"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="inline-flex items-center gap-2 text-sm font-semibold">
                  <ClipboardCheck className="h-4 w-4"/> Manual Copy Required
                </div>
                <button className="rounded-lg p-1 hover:bg-white/10" onClick={() => setManualOpen(false)} aria-label="Close">
                  <X className="h-4 w-4"/>
                </button>
              </div>
              <p className="text-xs text-white/80 mb-2">Your browser blocked programmatic clipboard access here. Press <kbd>Ctrl</kbd>+<kbd>C</kbd> (<kbd>⌘</kbd>+<kbd>C</kbd> on Mac) to copy the text below.</p>
              <textarea
                ref={textRef}
                readOnly
                value={text}
                className="w-full min-h-24 rounded-lg bg-black/30 border border-white/15 p-3 text-xs"
              />
              <div className="mt-3 flex items-center justify-end gap-2">
                <button onClick={() => { if (textRef.current) { textRef.current.focus(); textRef.current.select(); } }} className="rounded-xl px-3 py-2 text-sm border border-white/20 bg-white/10 hover:bg-white/15">Select All</button>
                <button onClick={() => setManualOpen(false)} className="rounded-xl px-3 py-2 text-sm border border-white/20 bg-white/10 hover:bg-white/15">Done</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ---------- Main Component ----------
export default function ColorPickerApp({ initial = "#ff69b4", attachToRoot = false, onChange }) {
  const [color, setColor] = useState(initial);
  const [swatches, setSwatches] = useState(["#ff69b4", "#7c3aed", "#06b6d4", "#22c55e", "#f59e0b"]);
  const liveRef = useRef(null);

  const rgb = useMemo(() => hexToRgb(color), [color]);
  const hsl = useMemo(() => rgbToHsl(rgb.r, rgb.g, rgb.b), [rgb]);

  const cssVarSnippet = useMemo(() => `:root{
  --brand: ${color};
  --brand-rgb: ${rgb.r} ${rgb.g} ${rgb.b};
  --brand-hsl: ${hsl.h} ${hsl.s}% ${hsl.l}%;
}

.button{ background: var(--brand); }
.text{ color: hsl(var(--brand-hsl)); }
.shadowy{ box-shadow: 0 8px 24px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.35); }`, [color, rgb, hsl]);

  useEffect(() => {
    if (!attachToRoot) return;
    const root = document.documentElement;
    try {
      root.style.setProperty("--brand", color);
      root.style.setProperty("--brand-rgb", `${rgb.r} ${rgb.g} ${rgb.b}`);
      root.style.setProperty("--brand-hsl", `${hsl.h} ${hsl.s}% ${hsl.l}%`);
    } catch (e) {
      // Extremely defensive: if the environment somehow forbids style mutation, just ignore.
      console.warn("Failed to write CSS variables to :root", e);
    }
  }, [attachToRoot, color, rgb, hsl]);

  useEffect(() => { onChange?.(color); }, [color, onChange]);

  const palette = useMemo(() => ([-30, -15, 0, 15, 30].map((p) => shade(color, p))), [color]);

  const announce = (msg) => { if (liveRef.current) liveRef.current.textContent = msg; };
  const addSwatch = () => { setSwatches((prev) => (prev.includes(color) ? prev : [color, ...prev].slice(0, 12))); announce("Added swatch"); };
  const removeSwatch = (hex) => { setSwatches((prev) => prev.filter((c) => c !== hex)); announce("Removed swatch"); };

  return (
    <div className="w-full h-full p-6 flex items-center justify-center bg-gradient-to-br from-gray-900 via-purple-900/20 to-pink-900/20 text-white overflow-auto">
      <div className="max-w-4xl w-full">
        <motion.div layout className="rounded-3xl overflow-hidden shadow-2xl border border-white/20 glass-effect">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-white/15">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 grid place-items-center rounded-2xl bg-white/10 border border-white/20">
                <Droplet className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base md:text-lg font-semibold leading-tight">Glass Color Picker</h2>
                <p className="text-xs text-white/70">Pick a color, copy CSS, create magic.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { const hx = randomHex(); setColor(hx); announce("Random color chosen"); }}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium border border-white/20 bg-white/10 hover:bg-white/15 active:scale-95 transition"
              >
                <Shuffle className="h-4 w-4"/> Random
              </button>
              <button
                type="button"
                onClick={addSwatch}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium border border-white/20 bg-white/10 hover:bg-white/15 active:scale-95 transition"
              >
                <Plus className="h-4 w-4"/> Save
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="grid md:grid-cols-2 gap-6 p-6">
            {/* Picker + preview */}
            <div className="space-y-5">
              <label className="block text-sm font-medium text-white/85">Choose color</label>
              <div className="flex items-center gap-4">
                <div className="glass-effect rounded-xl p-2 border border-white/20">
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    aria-label="Choose color"
                    className="h-12 w-20 cursor-pointer rounded-md bg-transparent p-0 [appearance:auto]"
                  />
                </div>
                <input
                  type="text"
                  value={color}
                  onChange={(e) => { const v = e.target.value.trim(); if (/^#([0-9a-fA-F]{3}){1,2}$/.test(v)) setColor(v); }}
                  aria-label="Hex value"
                  className="flex-1 rounded-xl glass-effect border border-white/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white/20 bg-white/5"
                />
                {typeof window !== "undefined" && "EyeDropper" in window && (
                  <button
                    type="button"
                    onClick={async ()=>{ try { const eye = new window.EyeDropper(); const res = await eye.open(); if (res?.sRGBHex) setColor(res.sRGBHex); } catch(e){ /* user canceled or not available */ } }}
                    title="Pick from screen"
                    className="rounded-xl px-3 py-2 border border-white/20 bg-white/10 hover:bg-white/15 active:scale-95 transition"
                  >
                    <Pipette className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-4">
                <div className="relative">
                  <motion.div
                    key={color}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 240, damping: 20 }}
                    className="h-28 w-28 rounded-3xl shadow-xl border border-white/20"
                    style={{ background: color }}
                    aria-label="Preview swatch"
                  />
                  <div className="absolute -inset-1 rounded-3xl pointer-events-none"/>
                </div>
                <div className="grid grid-cols-5 gap-2 flex-1">
                  {palette.map((p) => (
                    <button
                      key={p}
                      className="h-10 rounded-xl border border-white/20 glass-effect"
                      style={{ background: p }}
                      title={p}
                      onClick={() => setColor(p)}
                    />
                  ))}
                </div>
              </div>

              {/* Saved swatches */}
              {swatches.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-white/70">Saved swatches</p>
                  <div className="flex flex-wrap gap-2">
                    {swatches.map((s) => (
                      <div key={s} className="group relative">
                        <button
                          className="h-8 w-8 rounded-md border border-white/20 glass-effect"
                          style={{ background: s }}
                          onClick={() => setColor(s)}
                          title={s}
                        />
                        <button
                          aria-label={`Remove ${s}`}
                          onClick={() => removeSwatch(s)}
                          className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition bg-black/60 rounded-full p-1"
                        >
                          <Trash2 className="h-3 w-3"/>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Codes */}
            <div className="space-y-5">
              <div className="glass-effect rounded-2xl border border-white/20 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold">HEX</h3>
                  <CopyButton text={color} label="Copy HEX" />
                </div>
                <pre className="rounded-lg bg-black/30 border border-white/10 p-3 text-sm overflow-x-auto">{color}</pre>
              </div>
              <div className="glass-effect rounded-2xl border border-white/20 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold">RGB</h3>
                  <CopyButton text={rgbString(rgb)} label="Copy RGB" />
                </div>
                <pre className="rounded-lg bg-black/30 border border-white/10 p-3 text-sm overflow-x-auto">{rgbString(rgb)}</pre>
              </div>
              <div className="glass-effect rounded-2xl border border-white/20 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold">HSL</h3>
                  <CopyButton text={hslString(hsl)} label="Copy HSL" />
                </div>
                <pre className="rounded-lg bg-black/30 border border-white/10 p-3 text-sm overflow-x-auto">{hslString(hsl)}</pre>
              </div>
              <div className="glass-effect rounded-2xl border border-white/20 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold">CSS Variables + Usage</h3>
                  <CopyButton text={cssVarSnippet} label="Copy CSS vars" />
                </div>
                <pre className="rounded-lg bg-black/30 border border-white/10 p-3 text-xs leading-relaxed overflow-x-auto whitespace-pre">{cssVarSnippet}</pre>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-5 border-t border-white/15 text-xs text-white/75">
            Pro tip: Use the eyedropper to sample colors from anywhere on your screen! 🎨
          </div>
        </motion.div>
        <p ref={liveRef} className="sr-only" aria-live="polite" />
      </div>
    </div>
  );
}

// ---------- Tiny test panel (manual render) ----------
export function GlassColorPickerTestPanel() {
  const tests = [
    {
      name: "hexToRgb full #ff69b4",
      run: () => JSON.stringify(hexToRgb("#ff69b4")) === JSON.stringify({ r: 255, g: 105, b: 180 }),
    },
    {
      name: "hexToRgb short #0cf",
      run: () => JSON.stringify(hexToRgb("#0cf")) === JSON.stringify({ r: 0, g: 204, b: 255 }),
    },
    {
      name: "rgbToHsl for #ff69b4 ≈ hsl(330,100%,71%)",
      run: () => {
        const { h, s, l } = rgbToHsl(255, 105, 180);
        return Math.abs(h - 330) <= 1 && s >= 98 && s <= 100 && l >= 70 && l <= 72;
      },
    },
    {
      name: "shade lighter from #000 at +30%",
      run: () => shade("#000000", 30).toLowerCase() === "#4d4d4d",
    },
    {
      name: "shade darker from #fff at -30%",
      run: () => shade("#ffffff", -30).toLowerCase() === "#b3b3b3",
    },
  ];

  const results = tests.map((t) => ({ name: t.name, pass: !!t.run() }));

  return (
    <div className="p-4 glass-effect rounded-2xl border border-white/20 text-sm">
      <div className="font-semibold mb-2">GlassColorPicker Tests</div>
      <ul className="space-y-1">
        {results.map((r) => (
          <li key={r.name} className={r.pass ? "text-emerald-300" : "text-rose-300"}>
            {r.pass ? "✓" : "✗"} {r.name}
          </li>
        ))}
      </ul>
    </div>
  );
}
